import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, LoadingBlock, EmptyState, formatDate } from "@/components/Common";
import { useL } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const LEAD_STATUS = ["new", "qualified", "unqualified", "duplicate", "closed"];

export default function AdminLeads() {
  const L = useL();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/leads", { params: { q: q || undefined, status: status || undefined } });
    setItems(data.items);
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (id, s) => { await api.patch(`/leads/${id}`, { status: s }); toast.success(L("Stato aggiornato", "Status updated", "Status aktualisiert")); load(); };
  const convert = async (id) => {
    try { await api.post(`/leads/${id}/convert`); toast.success(L("Convertito in pratica", "Converted to case", "In Fall umgewandelt")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Lead", "Leads", "Leads")}</h2>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Cerca per email o nome…", "Search by email or name…", "Nach E-Mail oder Name suchen…")} className="pl-9 bg-navy-paper border-white/10" data-testid="leads-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white" data-testid="leads-status-filter">
          <option value="">{L("Tutti", "All", "Alle")}</option>
          {LEAD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={Inbox} title={L("Nessun lead", "No leads", "Keine Leads")} /> : (
        <div className="space-y-3">
          {items.map((l) => (
            <Card key={l.id} className="p-5" data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-white font-medium">{l.first_name} {l.last_name}</p>
                  <p className="text-sm text-slate-400">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                  <p className="text-xs text-slate-500 mt-1">{l.country} · {formatDate(l.created_at)} · <span className="text-cyanx">{l.status}</span></p>
                  {l.message && <p className="mt-2 text-sm text-slate-300 max-w-xl">{l.message}</p>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="h-9 rounded-md bg-navy border border-white/10 px-2 text-sm text-white" data-testid={`lead-status-${l.id}`}>
                    {LEAD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {!l.converted_case_id ? (
                    <Button size="sm" className="bg-gold text-navy hover:bg-gold-hover" onClick={() => convert(l.id)} data-testid={`lead-convert-${l.id}`}><UserPlus className="h-4 w-4 mr-1.5" />{L("Converti in pratica", "Convert to case", "In Fall umwandeln")}</Button>
                  ) : <span className="text-xs text-emerald-400">{L("Convertito", "Converted", "Umgewandelt")}</span>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
